// Parental Controls — register children + set content filters
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Shield, UserPlus, Lock, AlertTriangle, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ParentalControls() {
  const { user } = useAuth();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/parental/children").then(({ data }) => setKids(data)).catch(() => setKids([]));
    setLoading(false);
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return (
      <div className="p-20 text-center space-y-3" data-testid="parental-signin">
        <Shield className="w-12 h-12 mx-auto text-nexus-cyan" />
        <h2 className="font-display text-2xl">Sign in to manage parental controls</h2>
        <Link to="/auth" className="btn-nexus inline-flex">Enter Nexus</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="parental-controls-page">
      <Toaster theme="dark" position="top-center" />
      <header className="space-y-2">
        <div className="label-eyebrow text-nexus-cyan">// parental controls · safety first</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter flex items-center gap-3">
          <Shield className="w-8 h-8 text-nexus-cyan" /> Keep your kids safe on NEXUS
        </h1>
        <p className="text-white/60 max-w-2xl text-sm leading-relaxed">
          Children under 14 require a verified parent account. Use this dashboard to register kid profiles, lock them to family-friendly categories, set daily watch limits, and block specific creators. Any nudity or repeat profanity gets auto-escalated — you&rsquo;ll be notified instantly.
        </p>
      </header>

      <ParentKidTutorial />

      <RegisterChildCard onCreated={load} />

      <section className="space-y-4" data-testid="children-list">
        <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2">
          <Lock className="w-4 h-4 text-nexus-violet" /> Your children
        </h2>
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && kids.length === 0 && (
          <div className="glass p-6 text-white/50 text-sm">No children registered yet. Use the form above to add a kid account.</div>
        )}
        <div className="grid lg:grid-cols-2 gap-px bg-white/5">
          {kids.map((k) => <ChildCard key={k.id} kid={k} />)}
        </div>
      </section>

      <SafetyInfo />
    </div>
  );
}

function ParentKidTutorial() {
  const steps = [
    { n: 1, title: "Create your parent account", body: "Sign up at /auth as yourself first. Your email, full legal name, ID number, PayPal and tax ID will be saved to your account — required once for any kids under 14.", icon: "👤" },
    { n: 2, title: "Verify your identity", body: "Open /verify and submit your KYC documents. Children under 14 cannot stream until the parent KYC is approved. Verification is usually under 24 hours.", icon: "🛡" },
    { n: 3, title: "Add the kid account", body: "Use the form below. Pick a kid email + password (you'll keep these). For under-14 kids, your KYC details auto-attach. Their wallet payouts route through your PayPal.", icon: "➕" },
    { n: 4, title: "Set the rules", body: "Inside each kid's card: allowed categories (gaming, art, music…), daily watch cap in minutes, and blocked creators. Any nudity or repeat profanity auto-escalates to your inbox.", icon: "🔒" },
    { n: 5, title: "Get weekly digests", body: "Every Sunday we email you each kid's full activity log — watch time, tips, subs, blocked content, level-ups. No surprises.", icon: "📨" },
  ];
  return (
    <section className="glass p-5 lg:p-6 space-y-4 border-l-2 border-nexus-cyan" data-testid="parent-kid-tutorial">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow text-nexus-cyan">// how to create a parent profile for kids</div>
          <h3 className="font-display font-bold text-base lg:text-lg mt-1">5 steps · all under 5 minutes</h3>
        </div>
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-display">read-only walkthrough</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {steps.map((s) => (
          <div key={s.n} className="bg-nexus-surface/60 border border-white/10 p-4 space-y-2 relative" data-testid={`tutorial-step-${s.n}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-2xl">{s.icon}</span>
              <span className="text-[10px] tracking-[0.3em] uppercase text-nexus-cyan font-display">Step {s.n}</span>
            </div>
            <div className="font-display text-sm font-bold leading-snug">{s.title}</div>
            <p className="text-[11px] text-white/55 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/50">Once your account is verified, scroll down to use the <span className="text-white">Register a child</span> form. We&rsquo;ll auto-attach your details for under-14 kids — you won&rsquo;t need to retype them.</p>
    </section>
  );
}

function RegisterChildCard({ onCreated }) {
  const [form, setForm] = useState({
    child_email: "", child_password: "", child_display_name: "", child_age: 10,
    parent_email: "", parent_full_name: "", parent_paypal_email: "", parent_tax_id: "", parent_id_number: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.child_email || !form.child_password || !form.child_display_name) {
      toast.error("Child email, name and password required");
      return;
    }
    if (form.child_age < 14) {
      const missing = [];
      if (!form.parent_full_name)   missing.push("Your full legal name");
      if (!form.parent_email)       missing.push("Your email");
      if (!form.parent_id_number)   missing.push("Your ID number");
      if (!form.parent_paypal_email)missing.push("Your PayPal");
      if (!form.parent_tax_id)      missing.push("Your tax ID");
      if (missing.length) {
        toast.error(`Under-14 needs: ${missing.join(", ")}`);
        return;
      }
    }
    setBusy(true);
    try {
      await api.post("/parental/register-child", form);
      toast.success(`✓ ${form.child_display_name} added — payout details saved to your account`);
      setForm({ ...form, child_email: "", child_password: "", child_display_name: "", child_age: 10 });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register child");
    }
    setBusy(false);
  };

  return (
    <div className="glass p-6 space-y-4 border-l-2 border-nexus-cyan" data-testid="register-child-card">
      <div className="flex items-center gap-2">
        <UserPlus className="w-5 h-5 text-nexus-cyan" />
        <div className="label-eyebrow text-nexus-cyan">// register a child · you receive their funds</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <input value={form.child_display_name} onChange={(e) => setForm({ ...form, child_display_name: e.target.value })}
          placeholder="Child display name" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="child-name-input" />
        <input type="number" min={5} max={17} value={form.child_age}
          onChange={(e) => setForm({ ...form, child_age: parseInt(e.target.value || "0", 10) })}
          placeholder="Age" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="child-age-input" />
        <input value={form.child_email} onChange={(e) => setForm({ ...form, child_email: e.target.value })}
          placeholder="Child email (sign-in)" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="child-email-input" />
        <input type="password" value={form.child_password} onChange={(e) => setForm({ ...form, child_password: e.target.value })}
          placeholder="Child password" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="child-password-input" />
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <div className="label-eyebrow text-nexus-gold">// your details — required for under-14 (funds &amp; tax)</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.parent_full_name} onChange={(e) => setForm({ ...form, parent_full_name: e.target.value })}
            placeholder="Your full legal name" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="parent-name-input" />
          <input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
            placeholder="Your email (digests)" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="parent-email-input" />
          <input value={form.parent_paypal_email} onChange={(e) => setForm({ ...form, parent_paypal_email: e.target.value })}
            placeholder="Your PayPal email (receive funds)" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="parent-paypal-input" />
          <input value={form.parent_tax_id} onChange={(e) => setForm({ ...form, parent_tax_id: e.target.value })}
            placeholder="Your Tax ID / IRD / TFN" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm" data-testid="parent-tax-input" />
          <input value={form.parent_id_number} onChange={(e) => setForm({ ...form, parent_id_number: e.target.value })}
            placeholder="Your ID number (KYC)" className="bg-transparent border border-white/10 px-3 py-2.5 text-sm sm:col-span-2" data-testid="parent-id-input" />
        </div>
      </div>

      <button onClick={submit} disabled={busy} className="btn-nexus" data-testid="register-child-btn">
        <UserPlus className="w-3.5 h-3.5" /> {busy ? "Registering…" : "Register child"}
      </button>
      <p className="text-[11px] text-white/40 leading-relaxed">
        Children under 14 are auto-locked to <strong className="text-nexus-mint">kid-safe categories</strong>. All NX earnings, tips received, ad impression bounties and subscriptions go to <strong>your PayPal</strong>. Weekly digest emails recap balance + analytics.
      </p>
    </div>
  );
}

function ChildCard({ kid }) {
  const [filter, setFilter] = useState(kid.content_filter || "kids");
  const [mins, setMins] = useState(kid.max_daily_minutes || 60);
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digest, setDigest] = useState(null);

  const fetchDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.get(`/parental/digest/${kid.id}`);
      setDigest(data);
    } catch (e) { toast.error("Digest failed"); }
    setDigestBusy(false);
  };

  const emailDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post(`/parental/digest/email/${kid.id}`);
      if (data.sent) toast.success(`Weekly digest emailed to you`);
      else toast.error(data.reason || "Email send failed");
      setDigest(data.digest);
    } catch (e) { toast.error("Email digest failed"); }
    setDigestBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/parental/controls", {
        child_id: kid.id,
        content_filter: filter,
        max_daily_minutes: mins,
      });
      toast.success(`Updated controls for ${kid.display_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  return (
    <div className="bg-nexus-surface p-5 space-y-4" data-testid={`child-${kid.id}`}>
      <div className="flex items-center gap-3">
        <img src={kid.avatar} alt="" className="w-12 h-12 ring-1 ring-white/20 object-cover" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base">{kid.display_name}</div>
          <div className="text-[11px] text-white/40">{kid.email} · age {kid.age}</div>
        </div>
        {kid.swear_offences > 0 && (
          <div className="text-[10px] tracking-wider px-2 py-1 border border-nexus-amber text-nexus-amber" data-testid={`swear-warn-${kid.id}`}>
            ⚠ {kid.swear_offences} offence{kid.swear_offences > 1 ? "s" : ""}
          </div>
        )}
        {kid.suspended && (
          <div className="text-[10px] tracking-wider px-2 py-1 border border-nexus-crimson text-nexus-crimson">SUSPENDED</div>
        )}
      </div>

      <div className="space-y-2">
        <div className="label-eyebrow">// content filter</div>
        <div className="grid grid-cols-3 gap-1">
          {["kids", "teen", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[10px] tracking-[0.2em] uppercase py-2 border transition-colors ${
                filter === f ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/10" : "border-white/10 text-white/50 hover:text-white"
              }`}
              data-testid={`filter-${f}-${kid.id}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="label-eyebrow flex items-center justify-between">
          <span>// daily watch limit</span>
          <span className="text-nexus-amber font-mono">{mins} min</span>
        </div>
        <input
          type="range" min={15} max={240} step={15}
          value={mins}
          onChange={(e) => setMins(parseInt(e.target.value, 10))}
          className="w-full"
          data-testid={`mins-${kid.id}`}
        />
      </div>


      <button onClick={save} disabled={busy} className="btn-nexus w-full justify-center" data-testid={`save-${kid.id}`}>
        <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save controls"}
      </button>

      <div className="border-t border-white/10 pt-3 space-y-2" data-testid={`digest-block-${kid.id}`}>
        <div className="label-eyebrow text-nexus-violet">// weekly digest</div>
        <div className="flex gap-2">
          <button onClick={fetchDigest} disabled={digestBusy} className="btn-ghost text-[10px] flex-1 justify-center" data-testid={`digest-preview-${kid.id}`}>
            {digestBusy ? "Loading…" : "Preview"}
          </button>
          <button onClick={emailDigest} disabled={digestBusy} className="btn-ghost text-[10px] flex-1 justify-center !border-nexus-violet !text-nexus-violet" data-testid={`digest-email-${kid.id}`}>
            📧 Email it
          </button>
        </div>
        {digest && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/5 text-[10px]" data-testid={`digest-summary-${kid.id}`}>
              <div className="bg-black/30 p-2"><div className="text-white/40">NX balance</div><div className="font-display text-base text-nexus-gold">{(digest.nx_balance || 0).toFixed(0)}</div></div>
              <div className="bg-black/30 p-2"><div className="text-white/40">subs</div><div className="font-display text-base text-nexus-violet">{digest.subscriptions_active || 0}</div></div>
              <div className="bg-black/30 p-2"><div className="text-white/40">watch min</div><div className="font-display text-base text-nexus-cyan">{digest.watch_minutes}</div></div>
              <div className="bg-black/30 p-2"><div className="text-white/40">impressions</div><div className="font-display text-base text-nexus-amber">{digest.total_ad_impressions}</div></div>
              <div className="bg-black/30 p-2"><div className="text-white/40">bounties</div><div className="font-display text-base text-nexus-mint">{digest.bounty_count}</div></div>
            </div>
            <div className="bg-nexus-gold/10 border border-nexus-gold/40 p-3 text-xs flex items-center justify-between" data-testid={`paypal-funds-${kid.id}`}>
              <div>
                <div className="label-eyebrow text-nexus-gold">// funds received this week → your PayPal</div>
                <div className="text-white/50 mt-1">{(digest.parent_payout || {}).paypal_email || "set PayPal in your account"}</div>
              </div>
              <div className="font-display text-2xl text-nexus-gold">+{(digest.nx_in_period || 0).toFixed(0)} <span className="text-sm text-white/40">NX</span></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SafetyInfo() {
  return (
    <section className="glass p-6 space-y-3 border-l-2 border-nexus-amber" data-testid="safety-info">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-nexus-amber" />
        <div className="label-eyebrow text-nexus-amber">// how nexus protects kids</div>
      </div>
      <ul className="text-xs text-white/70 space-y-2 leading-relaxed">
        <li>· <strong>Automatic nudity ban.</strong> Any user attempting to title a stream with sexual content is auto-suspended pending owner review.</li>
        <li>· <strong>Kid swear filter.</strong> If a child swears in chat, you get a first-strike warning. A second offence escalates to staff and is logged for owner review.</li>
        <li>· <strong>Kid-safe categories.</strong> The <em>kids</em> filter limits viewing to family-safe categories — no IRL or mature content.</li>
        <li>· <strong>R18+ flag.</strong> Streamers must self-declare R18 content. Child accounts never see flagged streams.</li>
        <li>· <strong>Streaming locked.</strong> Children cannot go live or be advertised to as adults.</li>
      </ul>
    </section>
  );
}
